import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "@/Functions";
import axios from "@/Data/Axios";
import {
    UniversityListParams,
    TrackListParams,
    CreateUniversityRequest,
    UpdateUniversityRequest,
    CreateTrackRequest,
    UpdateTrackRequest,
    AdmissionApiResponse
} from "@/Data/Admission";
import {
    University,
    Track
} from 'ai-api/types/admission';

// Validation functions
const validateUniversity = (university: any): university is University => {
    return university && 
           typeof university.universityid === 'string' &&
           typeof university.name === 'string' &&
           typeof university.location === 'string' &&
           ['national', 'private', 'public'].includes(university.type);
};

const validateTrack = (track: any): track is Track => {
    return track &&
           typeof track.universityid === 'string' &&
           typeof track.trackid === 'string' &&
           typeof track.name === 'string' &&
           ['regular', 'early', 'special', 'rolling'].includes(track.type);
};

// Main state atom for university data
const universityState = atom({
    key: 'universityState' + randomString(4),
    default: {
        universities: {} as Record<string, University>,
        tracks: {} as Record<string, Track>,
        loading: false,
        error: null as string | null,
    }
});

/**
 * Hook for managing university and track data
 */
export const useUniversity = () => {
    const [state, setState] = useRecoilState(universityState);
    const overwriteState = useCallback((newState: Partial<typeof state>) => setState(prev => ({ ...prev, ...newState })), [setState]);

    const { universities, tracks, loading, error } = state;

    // Fetch universities from API
    const fetchUniversities = useCallback(async (params?: UniversityListParams): Promise<void> => {
        overwriteState({ loading: true, error: null });
        
        try {
            const queryParams: Record<string, string> = {};
            if (params?.universityid) {
                queryParams.universityid = params.universityid;
            }
            
            const response = await axios.get<AdmissionApiResponse<University[]>>('/admission/university', {
                params: queryParams
            });

            if (response.data.code !== 200 || !Array.isArray(response.data.data)) {
                throw new Error('Invalid response format');
            }

            // Validate universities
            const validUniversities = response.data.data.filter(validateUniversity);
            
            if (validUniversities.length !== response.data.data.length) {
                console.warn(`Filtered out ${response.data.data.length - validUniversities.length} invalid universities`);
            }

            // Update state with validated universities
            const universityMap = Object.fromEntries(validUniversities.map(uni => [uni.universityid, uni]));
            
            setState(prev => ({
                ...prev,
                universities: params?.universityid ? { ...prev.universities, ...universityMap } : universityMap,
                loading: false
            }));
            
        } catch (error) {
            console.error('Error fetching universities:', error);
            overwriteState({
                error: error instanceof Error ? error.message : "Failed to fetch universities",
                loading: false
            });
        }
    }, [setState, overwriteState]);

    // Fetch tracks from API
    const fetchTracks = useCallback(async (params?: TrackListParams): Promise<void> => {
        overwriteState({ loading: true, error: null });
        
        try {
            const queryParams: Record<string, string> = {};
            if (params?.universityid) {
                queryParams.universityid = params.universityid;
            }
            if (params?.trackid) {
                queryParams.trackid = params.trackid;
            }
            
            const response = await axios.get<AdmissionApiResponse<Track[]>>('/admission/track', {
                params: queryParams
            });

            if (response.data.code !== 200 || !Array.isArray(response.data.data)) {
                throw new Error('Invalid response format');
            }

            // Validate tracks
            const validTracks = response.data.data.filter(validateTrack);
            
            if (validTracks.length !== response.data.data.length) {
                console.warn(`Filtered out ${response.data.data.length - validTracks.length} invalid tracks`);
            }

            // Update state with validated tracks
            const trackMap = Object.fromEntries(validTracks.map(track => [track.trackid, track]));
            
            setState(prev => ({
                ...prev,
                tracks: { ...prev.tracks, ...trackMap },
                loading: false
            }));
            
        } catch (error) {
            console.error('Error fetching tracks:', error);
            overwriteState({
                error: error instanceof Error ? error.message : "Failed to fetch tracks",
                loading: false
            });
        }
    }, [setState, overwriteState]);

    // Create university
    const createUniversity = useCallback(async (data: CreateUniversityRequest): Promise<boolean> => {
        if (!data || typeof data !== 'object') {
            console.error('Invalid university data provided');
            return false;
        }

        overwriteState({ loading: true, error: null });
        
        try {
            const response = await axios.post<AdmissionApiResponse<University>>('/admission/university', data);

            if (response.data.code !== 200 || !validateUniversity(response.data.data)) {
                throw new Error('Invalid response format');
            }

            // Update state with new university
            setState(prev => ({
                ...prev,
                universities: { ...prev.universities, [response.data.data.universityid]: response.data.data },
                loading: false
            }));
            
            return true;
            
        } catch (error) {
            console.error('Error creating university:', error);
            overwriteState({
                error: error instanceof Error ? error.message : "Failed to create university",
                loading: false
            });
            return false;
        }
    }, [setState, overwriteState]);

    // Update university
    const updateUniversity = useCallback(async (data: UpdateUniversityRequest): Promise<boolean> => {
        if (!data || typeof data !== 'object' || !data.universityid) {
            console.error('Invalid university update data provided');
            return false;
        }

        overwriteState({ loading: true, error: null });
        
        try {
            const response = await axios.put<AdmissionApiResponse<University>>('/admission/university', data);

            if (response.data.code !== 200 || !validateUniversity(response.data.data)) {
                throw new Error('Invalid response format');
            }

            // Update state with updated university
            setState(prev => ({
                ...prev,
                universities: { ...prev.universities, [response.data.data.universityid]: response.data.data },
                loading: false
            }));
            
            return true;
            
        } catch (error) {
            console.error('Error updating university:', error);
            overwriteState({
                error: error instanceof Error ? error.message : "Failed to update university",
                loading: false
            });
            return false;
        }
    }, [setState, overwriteState]);

    // Create track
    const createTrack = useCallback(async (data: CreateTrackRequest): Promise<boolean> => {
        if (!data || typeof data !== 'object') {
            console.error('Invalid track data provided');
            return false;
        }

        overwriteState({ loading: true, error: null });
        
        try {
            const response = await axios.post<AdmissionApiResponse<Track>>('/admission/track', data);

            if (response.data.code !== 200 || !validateTrack(response.data.data)) {
                throw new Error('Invalid response format');
            }

            // Update state with new track
            const trackKey = `${response.data.data.universityid}-${response.data.data.trackid}`;
            setState(prev => ({
                ...prev,
                tracks: { ...prev.tracks, [trackKey]: response.data.data },
                loading: false
            }));
            
            return true;
            
        } catch (error) {
            console.error('Error creating track:', error);
            overwriteState({
                error: error instanceof Error ? error.message : "Failed to create track",
                loading: false
            });
            return false;
        }
    }, [setState, overwriteState]);

    // Update track
    const updateTrack = useCallback(async (data: UpdateTrackRequest): Promise<boolean> => {
        if (!data || typeof data !== 'object' || !data.universityid || !data.trackid) {
            console.error('Invalid track update data provided');
            return false;
        }

        overwriteState({ loading: true, error: null });
        
        try {
            const response = await axios.put<AdmissionApiResponse<Track>>('/admission/track', data);

            if (response.data.code !== 200 || !validateTrack(response.data.data)) {
                throw new Error('Invalid response format');
            }

            // Update state with updated track
            const trackKey = `${response.data.data.universityid}-${response.data.data.trackid}`;
            setState(prev => ({
                ...prev,
                tracks: { ...prev.tracks, [trackKey]: response.data.data },
                loading: false
            }));
            
            return true;
            
        } catch (error) {
            console.error('Error updating track:', error);
            overwriteState({
                error: error instanceof Error ? error.message : "Failed to update track",
                loading: false
            });
            return false;
        }
    }, [setState, overwriteState]);

    // Get tracks by university ID
    const getTracksByUniversity = useCallback((universityid: string): Track[] => {
        return Object.values(tracks).filter(track => track.universityid === universityid);
    }, [tracks]);

    const reset = useResetRecoilState(universityState);

    return {
        // Data objects
        universities,
        tracks,
        loading,
        error,
        
        // Fetch functions
        fetchUniversities,
        fetchTracks,
        
        // CRUD functions
        createUniversity,
        updateUniversity,
        createTrack,
        updateTrack,
        
        // Helper functions
        getTracksByUniversity,
        
        reset
    };
};